import React, { useEffect } from 'react';
import {
  MsalAuthProvider,
  SupportLoginController,
  useMsalAuth,
  useUserStoreAuthBridge,
  useUserStoreEntitlements,
  useUserStoreSnapshot,
  useUserStoreContextSync,
} from '@arm/ce-shared-components';
import { UseGlobalState } from '../../../context';
import { AZUREB2CSERVICE_INIT, CLEAR_USER_DATA } from '../../../constants/actionTypes';
import { getMsalAuthConfig, getPostAuthRedirectPath } from '../../../lib/msalAuthConfig';
import {
  getEntitlementsBaseUrl,
  getEntitlementsCacheKey,
  getEntitlementsCacheTtlMs,
  getIsAIAssistanceEnabled,
  getIsCheckEntitlementsEnabled,
} from '../../../config/virtualFAEConfig';
import { clearPendingDownload } from '../../../utils/pendingDownload';
import config from '../../../temp/config';

const SIGNOUT_EVENT = 'arm-account-signout';

const getSupportLoginOutroLink = () => `${config.REACT_APP_ORIGIN.replace(/\/$/, '')}/dashboard`;

const clearAbandonedPendingDownload = (reason) => {
  if (reason === 'dismiss' || reason === 'auth-failed') {
    clearPendingDownload();
  }
};

// Maps the MsalAuthentication user onto the shape the existing global state / global nav expect.
const mapMsalUser = (msalUser, authToken) => ({
  name: msalUser.name,
  displayableId: msalUser.email,
  email: msalUser.email,
  token: authToken,
  oid: msalUser.idTokenClaims?.oid,
});

// One-way bridge: mirrors the shared MSAL auth state into the existing user context so the
// global navigation (and other UseGlobalState consumers) reflect the MSAL user in support mode,
// and routes the nav's sign-out click to the MSAL logout.
const MsalUserBridge = () => {
  const authConfig = getMsalAuthConfig();
  const { user, isAuthenticated, isInitialized, logout, token } = useMsalAuth(authConfig);
  const { dispatch } = UseGlobalState();

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    const authToken = token ?? user?.idToken;
    if (isAuthenticated && user && authToken) {
      dispatch({
        type: AZUREB2CSERVICE_INIT,
        data: { user: mapMsalUser(user, authToken), isAuth: true, isLoginDone: true },
      });
    } else {
      dispatch({ type: CLEAR_USER_DATA });
    }
  }, [dispatch, isAuthenticated, isInitialized, token, user]);

  useEffect(() => {
    const handleSignOut = () => {
      logout();
    };
    document.addEventListener(SIGNOUT_EVENT, handleSignOut);
    return () => document.removeEventListener(SIGNOUT_EVENT, handleSignOut);
  }, [logout]);

  return null;
};

// Sibling bridge: writes the full `user-store` payload so any consumer (e.g.
// VirtualFAE) sees idToken + email + userContext + hasPackageEntitlements +
// timestamp regardless of which widgets are rendered on the current page.
// Lives here -- not inside VirtualFAE -- because not every support page
// renders the chat widget, but every support page must have an authoritative
// store snapshot post-login. Bails in iframes so it never competes with
// Support's own writer.
const UserStoreBridge = () => {
  useUserStoreAuthBridge();
  useUserStoreContextSync({
    isAIAssistanceEnabled: getIsAIAssistanceEnabled(),
    isCheckEntitlementsEnabled: getIsCheckEntitlementsEnabled(),
  });

  // Drive entitlement resolution from the shared CE hook. The hook owns the
  // user-store timestamp TTL check and only calls the entitlement service when
  // the persisted value is missing or stale.
  const snapshot = useUserStoreSnapshot();
  const idToken = snapshot?.idToken ?? null;
  const isCheckEntitlementsEnabled = snapshot?.userContext?.isCheckEntitlementsEnabled ?? false;

  useUserStoreEntitlements(
    idToken,
    getEntitlementsBaseUrl(),
    isCheckEntitlementsEnabled && !!idToken,
    {
      cacheKey: getEntitlementsCacheKey(),
      cacheTtlMs: getEntitlementsCacheTtlMs(),
    }
  );

  return null;
};

// Mounted only on support-tagged pages (see GlobalNavigation). Provides the MSAL/B2C login
// experience: silent SSO, the auto-opening login modal, and the user/logout bridge.
const SupportAuth = () => {
  const authConfig = getMsalAuthConfig();

  return (
    <MsalAuthProvider authConfig={authConfig}>
      <SupportLoginController
        allowCloseOnOverlayClick={false}
        onModalClose={clearAbandonedPendingDownload}
        postAuthRedirectPath={getPostAuthRedirectPath()}
        dictionary={{
          outroLink: getSupportLoginOutroLink(),
        }}
      />
      <MsalUserBridge />
      <UserStoreBridge />
    </MsalAuthProvider>
  );
};

export default SupportAuth;
